import clipping from 'polygon-clipping';
import { siteLevels, siteBuildings } from './siteBuildings.js';
import { isGroundLevel } from './siteGround.js';

const EPS = 1e-7;
const LIMITS = { wallsPerPlan: 4000, areasPerPlan: 200, verticesPerArea: 128, polygonComparisons: 250, boxChecks: 2000000 };
const cross = (ax, ay, bx, by) => ax * by - ay * bx;
const point = p => p && Number.isFinite(p.x) && Number.isFinite(p.y) && Math.abs(p.x) <= 100000 && Math.abs(p.y) <= 100000;
const bounds = points => ({ x0: Math.min(...points.map(p => p.x)), y0: Math.min(...points.map(p => p.y)), x1: Math.max(...points.map(p => p.x)), y1: Math.max(...points.map(p => p.y)) });
const boxesMeet = (a, b, pad = 0) => a.x0 <= b.x1 + pad && b.x0 <= a.x1 + pad && a.y0 <= b.y1 + pad && b.y0 <= a.y1 + pad;
const joinedBox = (a, b) => ({ x0: Math.min(a.x0, b.x0), y0: Math.min(a.y0, b.y0), x1: Math.max(a.x1, b.x1), y1: Math.max(a.y1, b.y1) });
// Group existing geometry by X while retaining original indices for stable
// finding order. A subtree's union box can reject many distant walls at once.
function spatialTree(items) {
  const ordered = items.map((item, index) => ({ item, index }))
    .sort((a, b) => (a.item.box.x0 + a.item.box.x1) - (b.item.box.x0 + b.item.box.x1) || a.index - b.index);
  const build = (start, end) => {
    if (start >= end) return null;
    const middle = Math.floor((start + end) / 2), entry = ordered[middle];
    const left = build(start, middle), right = build(middle + 1, end);
    let box = entry.item.box;
    if (left) box = joinedBox(box, left.box);
    if (right) box = joinedBox(box, right.box);
    return { ...entry, box, left, right };
  };
  return build(0, ordered.length);
}
const ringArea = ring => {
  const [ox, oy] = ring[0];
  return Math.abs(ring.reduce((sum, p, i) => { const q = ring[(i + 1) % ring.length]; return sum + cross(p[0] - ox, p[1] - oy, q[0] - ox, q[1] - oy); }, 0) / 2);
};
function intersects(a, b, c, d) {
  const side = (p, q, r) => cross(q.x - p.x, q.y - p.y, r.x - p.x, r.y - p.y);
  const on = (p, q, r) => Math.abs(side(p, q, r)) <= EPS && r.x >= Math.min(p.x, q.x) - EPS && r.x <= Math.max(p.x, q.x) + EPS && r.y >= Math.min(p.y, q.y) - EPS && r.y <= Math.max(p.y, q.y) + EPS;
  return side(a,b,c)*side(a,b,d)<0 && side(c,d,a)*side(c,d,b)<0 || on(a,b,c) || on(a,b,d) || on(c,d,a) || on(c,d,b);
}
function wallFinding(existing, proposed, tolerance) {
  const ax = existing.b.x-existing.a.x, ay = existing.b.y-existing.a.y, bx = proposed.b.x-proposed.a.x, by = proposed.b.y-proposed.a.y;
  const length = Math.hypot(ax,ay), otherLength = Math.hypot(bx,by), ux=ax/length, uy=ay/length;
  const dx=proposed.a.x-existing.a.x,dy=proposed.a.y-existing.a.y;
  const start=dx*ux+dy*uy, span=bx*ux+by*uy, low=Math.max(0,Math.min(start,start+span)), high=Math.min(length,Math.max(start,start+span));
  if (Math.abs(cross(ax,ay,bx,by))/(length*otherLength) <= Math.sin(3*Math.PI/180) && high-low > EPS && Math.abs(span)>EPS) {
    const at = along => Math.abs(cross(ux,uy,dx+bx*(along-start)/span,dy+by*(along-start)/span));
    const distance = Math.max(at(low),at(high));
    if(distance<=tolerance+EPS)return {kind:'near-wall',overlapMetres:high-low,distanceMetres:distance};
  }
  const den=cross(ax,ay,bx,by);
  if(Math.abs(den)>EPS){const t=cross(dx,dy,bx,by)/den,u=cross(dx,dy,ax,ay)/den;
    if(t*length>EPS&&(1-t)*length>EPS&&u*otherLength>EPS&&(1-u)*otherLength>EPS)return {kind:'crossing-wall',point:{x:existing.a.x+t*ax,y:existing.a.y+t*ay}};
  }
  return null;
}

/** Advisory review of two exact floor snapshots. No ownership inference, hulls,
 * snapping or mutation. Shared walls warn; shared footprint boundaries alone do
 * not. Counts describe only examined pairs when incomplete is true. */
export function reviewScanPlacement(destination, proposed, { wallTolerance=0.05, maxFindings=100, maxComparisons=50000, maxBoxChecks=LIMITS.boxChecks }={}) {
  if(!Number.isFinite(wallTolerance)||wallTolerance<0||wallTolerance>1||!Number.isInteger(maxFindings)||maxFindings<1||maxFindings>500||!Number.isInteger(maxComparisons)||maxComparisons<1||maxComparisons>100000||!Number.isInteger(maxBoxChecks)||maxBoxChecks<1||maxBoxChecks>LIMITS.boxChecks)throw new Error('Invalid scan placement review limits.');
  const result={findings:[],counts:{nearWalls:0,crossingWalls:0,areaOverlaps:0},proposedWallIDs:[],proposedAreaIDs:[],truncated:false,incomplete:false,reasons:[],comparisons:0,boxChecks:0};
  const issue=reason=>{result.incomplete=true;if(!result.reasons.includes(reason))result.reasons.push(reason);};
  const prepare=(plan,which)=>{
    const walls=[],areas=[];
    for(const [key,limit] of [['walls',LIMITS.wallsPerPlan],['areas',LIMITS.areasPerPlan]]){
      const input=plan?.[key]??[];
      if(!Array.isArray(input)){issue(`${which} ${key} are not a list.`);continue;}
      if(input.length>limit)issue(`${which} ${key} exceed the ${limit}-item review limit.`);
      const ids=new Set();
      for(const value of input.slice(0,limit)){
        if(key==='areas'&&(value?.kind!=='floor'||value.environment==='outdoor'))continue;
        if(!value||typeof value.id!=='string'||!value.id||ids.has(value.id)){issue(`${which} ${key} contain missing or duplicate IDs.`);continue;}ids.add(value.id);
        if(key==='walls'){
          const a={x:value.x1,y:value.y1},b={x:value.x2,y:value.y2};
          if(!point(a)||!point(b)||Math.hypot(b.x-a.x,b.y-a.y)<=EPS){issue(`${which} walls contain invalid or zero-length geometry.`);continue;}
          walls.push({id:value.id,a,b,box:bounds([a,b])});
        }else{
          if(!Array.isArray(value.pts)||value.pts.length<3||value.pts.length>LIMITS.verticesPerArea||!value.pts.every(point)) {issue(`${which} footprints contain invalid geometry or exceed the ${LIMITS.verticesPerArea}-vertex limit.`);continue;}
          const pts=value.pts.slice();if(pts.length>3&&pts[0].x===pts.at(-1).x&&pts[0].y===pts.at(-1).y)pts.pop();
          let invalid=false;
          for(let i=0;i<pts.length&&!invalid;i++){
            if(Math.hypot(pts[i].x-pts[(i+1)%pts.length].x,pts[i].y-pts[(i+1)%pts.length].y)<=EPS){invalid=true;break;}
            for(let j=i+2;j<pts.length;j++)if(!(i===0&&j===pts.length-1)&&intersects(pts[i],pts[(i+1)%pts.length],pts[j],pts[(j+1)%pts.length])){invalid=true;break;}
          }
          const ring=pts.map(p=>[p.x,p.y]);
          if(invalid||ringArea(ring)<=EPS){issue(`${which} footprints contain degenerate or self-intersecting geometry.`);continue;}
          areas.push({id:value.id,ring,box:bounds(pts)});
        }
      }
    }
    return {walls,areas};
  };
  const existing=prepare(destination,'Destination'),incoming=prepare(proposed,'Proposed');
  const add=(finding,e,p)=>{
    const key={ 'near-wall':'nearWalls','crossing-wall':'crossingWalls','area-overlap':'areaOverlaps' }[finding.kind];result.counts[key]++;
    if(result.findings.length>=maxFindings){result.truncated=true;issue(`More findings exist than the ${maxFindings}-finding display limit.`);return false;}
    result.findings.push({...finding,existingID:e.id,proposedID:p.id});return true;
  };
  const compare=()=>{if(result.comparisons>=maxComparisons){issue(`The ${maxComparisons}-pair comparison limit was reached.`);return false;}result.comparisons++;return true;};
  const checkBox=()=>{if(result.boxChecks>=maxBoxChecks){issue(`The ${maxBoxChecks}-pair spatial search budget was reached.`);return false;}result.boxChecks++;return true;};
  const nearby=(tree,proposed,pad)=>{
    const candidates=[];
    const visit=node=>{
      if(!node)return true;
      if(!checkBox())return false;
      if(!boxesMeet(node.box,proposed.box,pad))return true;
      if(boxesMeet(node.item.box,proposed.box,pad))candidates.push(node);
      return visit(node.left)&&visit(node.right);
    };
    if(!visit(tree))return null;
    candidates.sort((a,b)=>a.index-b.index);
    return candidates.map(node=>node.item);
  };
  let stopped=false;
  const wallTree=spatialTree(existing.walls);
  walls:for(const p of incoming.walls){
    const candidates=nearby(wallTree,p,wallTolerance);
    if(!candidates){stopped=true;break;}
    for(const e of candidates){
      if(!compare()){stopped=true;break walls;}
      const finding=wallFinding(e,p,wallTolerance);if(finding&&!add(finding,e,p)){stopped=true;break walls;}
    }
  }
  let polygonComparisons=0;
  const areaTree=spatialTree(existing.areas);
  if(!stopped)areas:for(const p of incoming.areas){
    const candidates=nearby(areaTree,p,0);
    if(!candidates)break;
    for(const e of candidates){
      if(!compare())break areas;
      if(polygonComparisons>=LIMITS.polygonComparisons){issue(`The ${LIMITS.polygonComparisons}-polygon comparison limit was reached.`);break areas;}polygonComparisons++;
      try{
        const intersections=clipping.intersection([e.ring],[p.ring]);
        const area=intersections.reduce((sum,polygon)=>sum+ringArea(polygon[0])-polygon.slice(1).reduce((holes,ring)=>holes+ringArea(ring),0),0);
        if(area>EPS&&!add({kind:'area-overlap',areaSquareMetres:area},e,p))break areas;
      }catch{issue('Some footprint intersections could not be evaluated.');}
    }
  }
  result.proposedWallIDs=[...new Set(result.findings.filter(f=>f.kind!=='area-overlap').map(f=>f.proposedID))];
  result.proposedAreaIDs=[...new Set(result.findings.filter(f=>f.kind==='area-overlap').map(f=>f.proposedID))];
  return result;
}

/** Ground is shared across buildings; upper floors and basements retain explicit
 * building/floor ownership. Never use a visible/composite Site as one floor. */
export function reviewSiteScanPlacement(source, geometry, { floorID=source.activeFloorId, newBuilding=false, wallTolerance=0.05, maxFindings=100, maxComparisons=50000, maxBoxChecks=LIMITS.boxChecks }={}) {
  const options={wallTolerance,maxFindings,maxComparisons,maxBoxChecks};
  const result=reviewScanPlacement({}, {}, options);
  if((source._siteLevels?.length??0)>128||(source.floors?.length??0)>127){
    result.incomplete=true;result.reasons.push('The Site exceeds the 128-floor review limit.');return result;
  }
  const levels=siteLevels(source),buildings=siteBuildings(source);
  const destination=levels.find(level=>level.id===floorID);
  if(!newBuilding&&!destination)throw new Error('Choose an existing destination floor before reviewing scan placement.');
  if(!newBuilding&&!Number.isFinite(destination.elevation))throw new Error('The destination floor has an invalid elevation.');
  const sharedGround=newBuilding||isGroundLevel(destination);
  const targets=sharedGround?levels.filter(level=>Number.isFinite(level.elevation)&&isGroundLevel(level)):[destination];
  if(sharedGround&&levels.some(level=>!Number.isFinite(level.elevation))){result.incomplete=true;result.reasons.push('Some Site floors have invalid elevations and could not be checked against shared ground.');}
  for(const target of targets){
    if(!target.plan||typeof target.plan!=='object'){
      result.incomplete=true;result.reasons.push(`Floor ${target.name||target.id} has no readable plan.`);continue;
    }
    const availableFindings=maxFindings-result.findings.length,availableComparisons=maxComparisons-result.comparisons,availableBoxChecks=maxBoxChecks-result.boxChecks;
    if(availableFindings<=0||availableComparisons<=0||availableBoxChecks<=0){
      result.incomplete=true;result.truncated ||= availableFindings<=0;
      result.reasons.push('The total Site review budget was reached; remaining ground floors were not checked.');break;
    }
    const reviewed=reviewScanPlacement(target.plan,geometry,{wallTolerance,maxFindings:availableFindings,maxComparisons:availableComparisons,maxBoxChecks:availableBoxChecks});
    const context={floorID:target.id,floorName:target.name,buildingId:target.buildingId,buildingName:buildings.find(building=>building.id===target.buildingId)?.name||'Building'};
    result.findings.push(...reviewed.findings.map(finding=>({...finding,...context})));
    for(const key of Object.keys(result.counts))result.counts[key]+=reviewed.counts[key];
    result.comparisons+=reviewed.comparisons;result.boxChecks+=reviewed.boxChecks;result.incomplete ||= reviewed.incomplete;result.truncated ||= reviewed.truncated;
    result.reasons.push(...reviewed.reasons.map(reason=>`${context.buildingName} · ${target.name}: ${reason}`));
  }
  result.proposedWallIDs=[...new Set(result.findings.filter(f=>f.kind!=='area-overlap').map(f=>f.proposedID))];
  result.proposedAreaIDs=[...new Set(result.findings.filter(f=>f.kind==='area-overlap').map(f=>f.proposedID))];
  return result;
}
